import React from 'react'
import {
    Button,
    ButtonViewMore,
    Col,
    colors,
    Images,
    OptionGroup,
    Paragraph,
    Row,
    Span,
    Typography,
    Utils
} from '@microfrontends/react-components'
import { EDevice, useStore } from '../../../../store'
import { formatAttachmentList } from '../../../../helpers/common'
import { AttachmentFileNameWrapper, RightButtonWrapper } from './makeProfileSearchable.css'
import { vnwBasePath } from '../constants'
import { useProfileSettingStore } from '../../../../store/ProfileSetting'
import { TResumeInfo } from '@microfrontends/react-components/components/hooks/useMakeSearchable/types'

interface IProps {
    trans: (value: any) => string
    searchableResume: Partial<TResumeInfo> | null
}

function Attachments({ trans, searchableResume }: IProps) {
    const { state } = useStore()

    const profileSettingStore = useProfileSettingStore()
    const settingState = profileSettingStore.state
    const { resumeId } = settingState
    const disPatchSettingState = profileSettingStore.setState

    const { profileSettingInfo, user, device } = state
    const isMobile = device === EDevice.MOBILE
    const { resumeSetting } = profileSettingInfo
    const attachment = resumeSetting.attachInfo
    const profileOnline = resumeSetting.profileInfo

    const allowSearchableAttachment = () => {
        return attachment.every((attachment) => attachment.allowSearchableDisplay !== 0)
    }

    const gotoProfileSetting = () => {
        window.location.assign(`${trans('/my-profile')}?tab=attached-cv`)
    }

    const handleOnClickAttachCV = (e) => {
        e.stopPropagation()
        const customEvent = new CustomEvent('CLOSE_PROFILE_SETTING_POPUP')
        window.dispatchEvent(customEvent)
    }

    const attachmentOptions = formatAttachmentList(attachment)

    let isSearchable = profileOnline.isSearchable === 1
    const filteredSearchable = attachment.filter((item) => item.isSearchable === 1)
    if (filteredSearchable.length > 0) {
        isSearchable = true
    }

    let currentResumeId = resumeId
    if (searchableResume !== null && searchableResume.id && !isSearchable) {
        currentResumeId = searchableResume.id
    }

    return (
        <>
            <Row>
                <Col xs='fill'>
                    <Typography name='label' size='medium' style={{ marginRight: 'auto' }} color={colors.grey[20]}>
                        {trans('By attached CVs')}
                    </Typography>
                </Col>
                {!isMobile ? (
                    <Col xs='auto'>
                        <Span
                            name='body'
                            size='small'
                            color={colors.primary[40]}
                            onClick={gotoProfileSetting}
                            style={{ marginRight: 0, cursor: 'pointer' }}
                        >
                            {trans('Manage attached CVs')}
                        </Span>
                    </Col>
                ) : null}
            </Row>
            {attachment && attachment.length > 0 && !allowSearchableAttachment() ? (
                <div>
                    <Button outline='outline' size='sm' type='info' onClick={(e) => handleOnClickAttachCV(e)}>
                        {trans('Complete basic information')}
                    </Button>
                </div>
            ) : null}
            <div>
                {attachment && attachment.length > 0 ? (
                    <>
                        {attachmentOptions.map((attachmentItem, index) => (
                            <OptionGroup
                                styles={{
                                    wrapperStyle: {
                                        // since ViewMoreButton popup is covered by other OptionGroup below,
                                        // current OptionGroup must have higher zIndex than the next one
                                        zIndex: attachmentOptions.length - index,
                                        marginBottom: index === attachmentOptions.length - 1 ? 0 : undefined
                                    }
                                }}
                                key={attachmentItem.id}
                                checked={currentResumeId === attachmentItem.id}
                                onChange={(id) => disPatchSettingState({ ...settingState, resumeId: Number(id) })}
                                contentDisable={false}
                                disabled={!allowSearchableAttachment()}
                                label={
                                    <AttachmentFileNameWrapper device={state.device}>
                                        <Span size='medium' name='paragraph'>
                                            {attachmentItem.name}
                                        </Span>
                                        <Span size='medium' name='paragraph'>
                                            .{attachmentItem.extension}
                                        </Span>
                                    </AttachmentFileNameWrapper>
                                }
                                value={attachmentItem.id}
                                name={attachmentItem.name}
                            >
                                <Typography name='label' size='small' color={colors.grey[60]}>
                                    {trans('Uploaded')} {attachmentItem.updatedDate}
                                </Typography>
                                <RightButtonWrapper
                                    active={
                                        attachmentItem.id === resumeId ||
                                        [EDevice.MOBILE, EDevice.TABLET].includes(state.device)
                                    }
                                >
                                    {[EDevice.LAPTOP, EDevice.DESKTOP].includes(state.device) ? (
                                        <>
                                            <Button
                                                type='info'
                                                size='sm'
                                                outline='outline'
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    Utils.downloadAttachFile({
                                                        resumeId: attachmentItem.id,
                                                        downloadRequest: 'new-window',
                                                        msBaseUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH ?? '',
                                                        accessToken: user?.accessToken ?? '',
                                                        fileName: ''
                                                    })
                                                }}
                                            >
                                                {trans('View CV')}
                                            </Button>
                                            <Button
                                                type='info'
                                                size='sm'
                                                outline='outline'
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    window.open(
                                                        `${vnwBasePath}/${trans('preview-resume')}/${attachmentItem?.id}`,
                                                        '_blank'
                                                    )
                                                }}
                                            >
                                                {trans('View as employer')}
                                            </Button>
                                        </>
                                    ) : (
                                        <ButtonViewMore
                                            onClick={(e) => e.stopPropagation()}
                                            textOptions={{
                                                closeText: trans('Close')
                                            }}
                                            header={
                                                <AttachmentFileNameWrapper device={state.device} fullWidth>
                                                    <Span size='medium' name='paragraph'>
                                                        {attachmentItem.name}
                                                    </Span>
                                                    <Span size='medium' name='paragraph'>
                                                        .{attachmentItem.extension}
                                                    </Span>
                                                </AttachmentFileNameWrapper>
                                            }
                                            options={[
                                                {
                                                    label: trans('View CV'),
                                                    icon: (
                                                        <Images.PDFIcon
                                                            width={16}
                                                            height={21}
                                                            className='pdf-icon'
                                                            color={colors.grey[50]}
                                                        />
                                                    ),
                                                    isCloseOnClick: true,
                                                    onClick: () => {
                                                        Utils.downloadAttachFile({
                                                            resumeId: attachmentItem.id,
                                                            downloadRequest: 'new-window',
                                                            msBaseUrl: process.env.NEXT_PUBLIC_MS_BASE_PATH ?? '',
                                                            accessToken: user?.accessToken ?? '',
                                                            fileName: ''
                                                        })
                                                    }
                                                },
                                                {
                                                    label: trans('View as employer'),
                                                    icon: (
                                                        <Images.EmployerViewIcon
                                                            width={16}
                                                            height={21}
                                                            color={colors.grey[50]}
                                                        />
                                                    ),
                                                    isCloseOnClick: true,
                                                    onClick: () => {
                                                        window.open(
                                                            `${vnwBasePath}/${trans('preview-resume')}/${attachmentItem?.id}`,
                                                            '_blank'
                                                        )
                                                    }
                                                }
                                            ]}
                                        />
                                    )}
                                </RightButtonWrapper>
                            </OptionGroup>
                        ))}
                    </>
                ) : null}
                {isMobile ? (
                    <Paragraph
                        name='body'
                        size='small'
                        color={colors.primary[40]}
                        onClick={gotoProfileSetting}
                        style={{ marginTop: 10, cursor: 'pointer' }}
                    >
                        {trans('Manage attached CVs')}
                    </Paragraph>
                ) : null}
            </div>
        </>
    )
}
export default React.memo(Attachments)
